import { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarRange, Loader2, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import LocationPicker from './LocationPicker';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// If user drags pin > 1km from auto-geocoded position, the typed address is
// likely unreliable. In that case keep only `citta` and clear `indirizzo`.
const GEOCODE_DRIFT_KM = 1;

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return d;
  }
}

const todayIso = () => new Date().toISOString().split('T')[0];

const emptyForm = () => ({
  indirizzo: '',
  citta: '',
  lat: null,
  lng: null,
  dal_giorno: todayIso(),
  al_giorno: todayIso(),
  dal_orario: '09:00',
  al_orario: '19:00',
  note: '',
});

export default function TemporaryLocationsControl({ locations = [], onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [geocodedAt, setGeocodedAt] = useState(null); // {lat, lng}
  const [deletingId, setDeletingId] = useState(null);

  const resetForm = () => {
    setForm(emptyForm());
    setGeocodedAt(null);
    setEditingId(null);
  };

  // Auto-geocode when indirizzo+citta change and user hasn't manually set the pin
  useEffect(() => {
    if (!showForm || editingId) return;
    if (form.lat || form.lng) return;
    if (!form.indirizzo || form.indirizzo.length < 3) return;
    const query = `${form.indirizzo}${form.citta ? `, ${form.citta}` : ''}, Italia`;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
          { headers: { 'Accept-Language': 'it' } }
        );
        const data = await res.json();
        if (!cancelled && data?.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          setForm((p) => (p.lat || p.lng ? p : { ...p, lat, lng }));
          setGeocodedAt({ lat, lng });
        }
      } catch (err) {
        // Nominatim can be slow/unreachable — non-blocking, user can still
        // set the pin manually.
        console.debug('Geocode failed:', err?.message);
      }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.indirizzo, form.citta, form.lat, form.lng, showForm, editingId]);

  const handleOpenEdit = (loc) => {
    setForm({
      indirizzo: loc.indirizzo || '',
      citta: loc.citta || '',
      lat: loc.lat || null,
      lng: loc.lng || null,
      dal_giorno: loc.dal_giorno || todayIso(),
      al_giorno: loc.al_giorno || todayIso(),
      dal_orario: loc.dal_orario || '09:00',
      al_orario: loc.al_orario || '19:00',
      note: loc.note || '',
    });
    setEditingId(loc.id);
    setGeocodedAt(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.lat || !form.lng) {
      toast.error('Seleziona la posizione sulla mappa');
      return;
    }
    if (!form.indirizzo && !form.citta) {
      toast.error('Inserisci almeno la città');
      return;
    }
    if (form.al_giorno < form.dal_giorno) {
      toast.error('La data di fine deve essere uguale o successiva a quella di inizio');
      return;
    }

    // If user dragged the pin far from the geocoded address, the typed
    // address is unreliable: keep only the city.
    let indirizzo = form.indirizzo;
    if (geocodedAt && form.lat && form.lng) {
      const drift = haversineKm(geocodedAt.lat, geocodedAt.lng, form.lat, form.lng);
      if (drift > GEOCODE_DRIFT_KM) {
        indirizzo = '';
        toast.info(`Posizione spostata di ${drift.toFixed(1)} km dall'indirizzo: salvata solo la città`);
      }
    }

    const payload = { ...form, indirizzo };
    setLoading(true);
    try {
      if (editingId) {
        // backend doesn't have an update endpoint - emulate: delete + add
        await axios.delete(`${API}/shops/my/temporary-locations/${editingId}`, { withCredentials: true });
        const res = await axios.post(`${API}/shops/my/temporary-locations`, payload, { withCredentials: true });
        onChange([...locations.filter((l) => l.id !== editingId), res.data]);
        toast.success('Posizione aggiornata');
      } else {
        const res = await axios.post(`${API}/shops/my/temporary-locations`, payload, { withCredentials: true });
        onChange([...locations, res.data]);
        toast.success('Posizione temporanea aggiunta');
      }
      setShowForm(false);
      resetForm();
    } catch (e) {
      console.error('Save temporary location failed:', e?.response?.status, e?.response?.data);
      toast.error(e?.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    const id = deletingId;
    setDeletingId(null);
    try {
      await axios.delete(`${API}/shops/my/temporary-locations/${id}`, { withCredentials: true });
      onChange(locations.filter((l) => l.id !== id));
      toast.success('Posizione eliminata');
    } catch (e) {
      console.error('Delete temporary location failed:', e?.response?.status, e?.response?.data);
      const status = e?.response?.status;
      if (status === 401) toast.error('Sessione scaduta. Effettua di nuovo il login.');
      else if (status === 404) toast.error('Posizione non trovata (forse già eliminata)');
      else toast.error(e?.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const Header = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <CalendarRange className="w-5 h-5 text-blue-600" />
        <h3 className="font-medium text-slate-800">Posizioni temporanee</h3>
      </div>
      {!showForm && (
        <Button
          type="button"
          size="sm"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
          data-testid="add-temp-location"
        >
          <Plus className="w-4 h-4 mr-1" /> Aggiungi
        </Button>
      )}
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="temp-locations">
      {Header}
      <p className="text-xs text-slate-500">
        Mercati ed eventi a cui parteciperai. I clienti vedranno la posizione attiva nei giorni e orari indicati.
      </p>

      {locations.length === 0 && !showForm && (
        <p className="text-sm text-slate-500 italic">Nessuna posizione temporanea inserita.</p>
      )}

      {locations.length > 0 && (
        <ul className="space-y-2">
          {locations.map((loc) => (
            <li
              key={loc.id}
              className="flex items-start gap-3 p-3 rounded-xl bg-slate-50"
              data-testid={`temp-loc-${loc.id}`}
            >
              <MapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  {loc.indirizzo || (loc.citta || 'Senza indirizzo')}
                </p>
                {loc.indirizzo && loc.citta && <p className="text-xs text-slate-600">{loc.citta}</p>}
                <p className="text-xs text-slate-500 mt-1">
                  Dal <strong>{formatDate(loc.dal_giorno)}</strong> ({loc.dal_orario}) al{' '}
                  <strong>{formatDate(loc.al_giorno)}</strong> ({loc.al_orario})
                </p>
                {loc.note && <p className="text-xs text-slate-500 italic mt-1">{loc.note}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleOpenEdit(loc)}
                  className="text-blue-600 hover:bg-blue-100 p-2 rounded-lg"
                  aria-label="Modifica"
                  data-testid={`temp-loc-edit-${loc.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeletingId(loc.id)}
                  className="text-red-500 hover:bg-red-100 p-2 rounded-lg"
                  aria-label="Elimina"
                  data-testid={`temp-loc-delete-${loc.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div className="space-y-3 pt-2 border-t border-slate-200" data-testid="temp-loc-form">
          <p className="text-sm font-semibold text-slate-700">
            {editingId ? 'Modifica posizione' : 'Nuova posizione temporanea'}
          </p>
          <div>
            <Label className="text-xs">Indirizzo (opzionale)</Label>
            <Input
              value={form.indirizzo}
              onChange={(e) => setForm((p) => ({ ...p, indirizzo: e.target.value }))}
              placeholder="es. Piazza Garibaldi, Mercato del sabato"
              className="rounded-xl mt-1"
              data-testid="temp-loc-indirizzo"
            />
          </div>
          <div>
            <Label className="text-xs">Città *</Label>
            <Input
              value={form.citta}
              onChange={(e) => setForm((p) => ({ ...p, citta: e.target.value }))}
              placeholder="es. Milano"
              className="rounded-xl mt-1"
              data-testid="temp-loc-citta"
            />
          </div>
          <div>
            <Label className="text-xs">Posizione sulla mappa *</Label>
            <p className="text-[11px] text-slate-500 mb-1">
              Si imposta automaticamente dall'indirizzo. Trascina il pin per affinare; se sposti troppo (oltre {GEOCODE_DRIFT_KM} km) salveremo solo la città.
            </p>
            <LocationPicker
              lat={form.lat}
              lng={form.lng}
              onChange={(lat, lng) => setForm((p) => ({ ...p, lat, lng }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Dal giorno *</Label>
              <Input
                type="date"
                value={form.dal_giorno}
                onChange={(e) => setForm((p) => ({ ...p, dal_giorno: e.target.value }))}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Al giorno *</Label>
              <Input
                type="date"
                value={form.al_giorno}
                onChange={(e) => setForm((p) => ({ ...p, al_giorno: e.target.value }))}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Dalle ore</Label>
              <Input
                type="time"
                value={form.dal_orario}
                onChange={(e) => setForm((p) => ({ ...p, dal_orario: e.target.value }))}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Alle ore</Label>
              <Input
                type="time"
                value={form.al_orario}
                onChange={(e) => setForm((p) => ({ ...p, al_orario: e.target.value }))}
                className="rounded-xl mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Note (opzionale)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              placeholder="es. Stand n.12 - lato fontana"
              className="rounded-xl mt-1"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setShowForm(false); resetForm(); }}
              className="flex-1 rounded-xl"
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
              data-testid="temp-loc-save"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? 'Aggiorna' : 'Salva'}
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa posizione?</AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione non può essere annullata. I clienti non vedranno più questo evento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="temp-loc-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="temp-loc-delete-confirm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
